import { AfterViewInit, Component, HostBinding, OnDestroy, OnInit, inject, effect, DestroyRef, signal, ElementRef, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "@progress/kendo-angular-dialog";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { map, Observable, Subscription } from "rxjs";
import type { DashboardSelectCanChangeArgs } from "@shared/dashboard-select";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { DashboardsService } from "../../shared/dashboards-service/dashboards.service";
import { UtilsService } from "../../shared/utils/utils.service";
import { SettingsBreadcrumbService } from "../../shared/settings-breadcrumb-service/settings-breadcrumb.service";
import { CancelConfirmationComponent } from "../settings-dashboard-management-component/cancel-confirmation/cancel-confirmation.component";
import { SettingMappingModelService } from "./setting-mapping-model.service";
import { AddEditComponent, AnalyticsService } from "@shared/index";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";
import { isJobsBilling } from "src/app/views-interfaces/ApplicationType.enum";
import { IDashboardModel } from "src/app/views-interfaces/dashboard-model.interface";
import { HighlightOriginStrategy, provideDragAndDrop } from "@app/drag-and-drop";

@Component({
    selector: "wk-settings-mapping-component",
    templateUrl: "./settings-mapping.component.html",
    styleUrl: "./settings-mapping.component.scss",
    providers: [SettingMappingModelService, provideDragAndDrop({ outOfBoundsStrategy: new HighlightOriginStrategy() })],
    standalone: false,
    host: { class: "wk-sm" },
})
export class SettingsMappingComponent implements OnInit, OnDestroy, AfterViewInit {
    settingMappingModelService = inject(SettingMappingModelService);
    private readonly dialogService = inject(DialogService);
    private readonly translate = inject(TranslateService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly modalService = inject(BsModalService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly breadcrumbService = inject(SettingsBreadcrumbService);
    private readonly router = inject(Router);
    private readonly dragAndDropService = inject(DragAndDropService);
    private readonly destroyRef = inject(DestroyRef);

    subscription: Subscription;
    dashboardChangedSubscription: Subscription;
    mappingViewmodel: any = {};
    tempCollumnId = 0;
    showScroll = false;
    thisIsIE = false;
    bsModalRef?: BsModalRef;
    isFormChanged = false;
    softDeletedColumns: any[] = [];
    isDashboardChanging = false;
    dragging = false;
    public isJobsBillingDashboard = false;
    currentDashboard: IDashboardModel;

    unassignedStatusList = viewChild("unassignedStatusList", { read: ElementRef<HTMLElement> });
    unassignedStatusListHasScrollbar = signal(false);

    @HostBinding("class.wk-drag-dragging") get isDragging() {
        return this.dragAndDropService.isDragging();
    }

    constructor() {
        const settingMappingModelService = this.settingMappingModelService;

        effect(() => {
            const unassignedStatusList = this.unassignedStatusList();
            if (!unassignedStatusList) return;

            const unassignedStatusListEl = unassignedStatusList.nativeElement as HTMLElement;
            this.unassignedStatusListHasScrollbar.set(unassignedStatusListEl.scrollWidth > unassignedStatusListEl.clientWidth);
            const ro = new ResizeObserver((entries) => {
                this.unassignedStatusListHasScrollbar.set(unassignedStatusListEl.scrollWidth > unassignedStatusListEl.clientWidth);
            });
            ro.observe(unassignedStatusListEl);

            this.destroyRef.onDestroy(() => ro.disconnect());
            return () => ro.disconnect();
        });

        this.subscription = settingMappingModelService.settingMappingModelDone$.subscribe(async (isLoaded) => {
            if (isLoaded === true) {
                await this.init();
            }
        });

        this.dashboardChangedSubscription = this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            await this.settingMappingModelService.init();
            await this.init();
        });
    }

    async init() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard?.ApplicationType);

        this.mappingViewmodel = this.settingMappingModelService.mappingViewmodel;
        if (this.mappingViewmodel?.unassigned) {
            this.mappingViewmodel.unassigned = this.settingMappingModelService?.mappingViewmodel?.unassigned?.filter((item) => item?.Active === true);
        }

        this.showScroll = false;
        this.helpersService.suppressSpinner();
    }

    onDrop() {
        this.isFormChanged = true;
        this.helpersService.mappingColumnChangeEmitter.next(true);
    }

    onCancel() {
        if (this.isFormChanged) {
            this.openPendingChangesDialogConfirmation().subscribe(async (result) => {
                if (result == 1) {
                    this.router.navigate(["/settings/main"]);
                } else if (result == 2) {
                    return;
                }
            });
        } else {
            this.router.navigate(["/settings/main"]);
        }
    }

    onBreadcrumbNavigate(event: { route: string; proceed: () => void }): void {
        if (this.isFormChanged) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result === 1) {
                    this.isFormChanged = false;
                    event.proceed();
                }
            });
        } else {
            event.proceed();
        }
    }

    ngOnInit() {
        this.breadcrumbService.setBreadcrumbs("mappingofstatuses");
        this.analyticsService.track("Access Mapping of statuses");
        if ("function" !== typeof DataTransfer.prototype.setDragImage) {
            this.thisIsIE = true;
        }
    }

    async ngAfterViewInit() {
        await this.settingMappingModelService.init();
        await this.init();
    }

    ngOnDestroy() {
        this.dashboardChangedSubscription.unsubscribe();
        this.subscription.unsubscribe();
    }

    async onSave() {
        if (this.settingMappingModelService.isSaving === 0) {
            await this.settingMappingModelService.savingData(this.mappingViewmodel);
            await Promise.all(
                this.softDeletedColumns
                    .filter((item) => item.column.Id && !item.column.tempCollumnId)
                    .map((item) => {
                        this.settingMappingModelService.onColumnDelete(item.column);
                    }),
            );
            this.softDeletedColumns = [];
            this.isFormChanged = false;
        }
    }

    onAddNewColumn(mapping, type: string) {
        if (!this.mappingViewmodel[type]) {
            this.mappingViewmodel[type] = [];
        }
        this.mappingViewmodel[type].push({
            Name: {
                "en-CA": mapping.Name["en-CA"],
                "fr-CA": mapping.Name["fr-CA"],
            },
            Order: 1,
            Statuses: [],
            State: type,
            tempCollumnId: ++this.tempCollumnId,
            Id: crypto.randomUUID(),
        });
        this.isFormChanged = true;
    }

    addNewColumn(type: string) {
        const self = this;
        const newMapping = {
            Name: { "en-CA": "", "fr-CA": "" },
            Order: 1,
            Statuses: [],
            State: type,
            tempCollumnId: ++this.tempCollumnId,
        };
        this.bsModalRef = this.modalService.show(AddEditComponent, {
            initialState: {
                title: "Settings.AddStep",
                data: UtilsService.recursiveDeepCopy(newMapping),

                callback: (mapping) => self.onAddNewColumn(mapping, type),
            },
            class: "custom-modal-rename-dashboard",
        });
    }

    onMappingStatusFormChanged(event) {
        this.isFormChanged = event;
    }

    onDeleteStep(event) {
        event.forEach((deletedColumn) => {
            this.softDeletedColumns.push({
                columnType: deletedColumn.columnType,
                column: deletedColumn.column,
                order: deletedColumn.order,
            });
        });
    }

    openPendingChangesDialogConfirmation(): Observable<number> {
        const dialog = this.dialogService.open({
            title: this.translate.instant("AccessPendingChanges.Title"),
            content: CancelConfirmationComponent,
            actions: [
                { text: this.translate.instant("General.Discard"), primary: true, choice: 1 },
                { text: this.translate.instant("General.Cancel"), choice: 2 },
            ],
            width: 600,
            cssClass: "access-pending-changes-dialog",
        });
        return dialog.result.pipe(
            map((result: any) => {
                return result.choice;
            }),
        );
    }

    canChangeDashboard(args: DashboardSelectCanChangeArgs) {
        if (this.isFormChanged) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result <= 2) {
                    if (result === 1) {
                        this.isDashboardChanging = true;
                        this.isFormChanged = false;
                        if (args) {
                            this.doChangeDashboard(args);
                        }
                    } else {
                        if (args) {
                            args.cancelAction();
                        }
                    }
                } else if (args) {
                    args.cancelAction();
                }
            });
        } else {
            this.isDashboardChanging = true;
            this.doChangeDashboard(args);
        }
    }

    doChangeDashboard(args) {
        try {
            args.action(args.dashboardId);
        } catch (e) {
            this.isDashboardChanging = false;
        }
    }

    dashboardChangeCompleted(args: boolean) {
        if (args == true) {
            this.isDashboardChanging = false;
        }
    }
}
